import React, { useRef, useEffect, useContext } from "react";
import styled, { css } from "styled-components";
import convertHexToRgba from "@kiwicom/orbit-design-tokens/lib/convertHexToRgba";
import defaultTheme from "../../defaultTheme";
import media from "../../utils/mediaQuery";
import Button from "../../Button";
import resolvePopoverPosition from "../helpers/resolvePopoverPosition";
import resolvePopoverHorizontal from "../helpers/resolvePopoverHorizontal";
import calculatePopoverPosition from "../helpers/calculatePopoverPosition";
import calculateVerticalPosition from "../helpers/calculateVerticalPosition";
import calculateHorizontalPosition from "../helpers/calculateHorizontalPosition";
import useDimensions from "../hooks/useDimensions";
import Translate from "../../Translate";
import transition from "../../utils/transition";
import useClickOutside from "../../hooks/useClickOutside";
import { ModalContext } from "../../Modal/ModalContext";
var StyledPopoverParent = styled.div.withConfig({
  displayName: "ContentWrapper__StyledPopoverParent",
  componentId: "v01g55-0"
})(["position:fixed;bottom:0;left:0;right:0;width:100%;box-sizing:border-box;border-top-left-radius:9px;border-top-right-radius:9px;background-color:", ";padding:", ";box-shadow:", ";overflow:auto;z-index:1000;transition:", ";transform:translateY(", ");max-height:", ";overflow-y:scroll;&:focus{outline:0;}", ""], function (_ref) {
  var theme = _ref.theme;
  return theme.orbit.backgroundModal;
}, function (_ref2) {
  var theme = _ref2.theme,
      noPadding = _ref2.noPadding;
  return noPadding ? 0 : theme.orbit.spaceMedium;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.orbit.boxShadowRaisedReverse;
}, transition(["opacity", "transform"], "fast", "ease-in-out"), function (_ref4) {
  var shownMobile = _ref4.shownMobile;
  return shownMobile ? "0%" : "100%";
}, function (_ref5) {
  var theme = _ref5.theme;
  return "calc(100% - ".concat(theme.orbit.spaceXLarge, ")");
}, media.largeMobile(css(["z-index:", ";position:", ";left:auto;right:auto;bottom:auto;width:", ";opacity:", ";transform:none;border-radius:", ";overflow:auto;box-shadow:", ";", " ", ""], function (_ref6) {
  var isInsideModal = _ref6.isInsideModal;
  return isInsideModal ? "1000" : "600";
}, function (_ref7) {
  var fixed = _ref7.fixed;
  return fixed ? "fixed" : "absolute";
}, function (_ref8) {
  var width = _ref8.width;
  return width ? "".concat(width) : "auto";
}, function (_ref9) {
  var shown = _ref9.shown;
  return shown ? "1" : "0";
}, function (_ref10) {
  var theme = _ref10.theme;
  return theme.orbit.borderRadiusNormal;
}, function (_ref11) {
  var theme = _ref11.theme;
  return theme.orbit.boxShadowRaised;
}, resolvePopoverPosition, resolvePopoverHorizontal)));
StyledPopoverParent.defaultProps = {
  theme: defaultTheme
};
var StyledPopoverContent = styled.div.withConfig({
  displayName: "ContentWrapper__StyledPopoverContent",
  componentId: "v01g55-1"
})([""]);
var StyledOverlay = styled.div.withConfig({
  displayName: "ContentWrapper__StyledOverlay",
  componentId: "v01g55-2"
})(["display:block;position:fixed;opacity:", ";top:0;left:0;right:0;width:100%;height:100%;background-color:", ";transition:opacity ", " ease-in-out;z-index:999;", ";"], function (_ref12) {
  var shown = _ref12.shown;
  return shown ? "1" : "0";
}, function (_ref13) {
  var theme = _ref13.theme;
  return convertHexToRgba(theme.orbit.paletteInkNormal, 60);
}, function (_ref14) {
  var theme = _ref14.theme;
  return theme.orbit.durationNormal;
}, media.largeMobile(css(["display:none;"])));
StyledOverlay.defaultProps = {
  theme: defaultTheme
};
var StyledPopoverClose = styled.div.withConfig({
  displayName: "ContentWrapper__StyledPopoverClose",
  componentId: "v01g55-3"
})(["padding:", ";padding-top:", ";", ""], function (_ref15) {
  var theme = _ref15.theme,
      noPadding = _ref15.noPadding;
  return noPadding ? theme.orbit.spaceMedium : 0;
}, function (_ref16) {
  var theme = _ref16.theme;
  return theme.orbit.spaceMedium;
}, media.largeMobile(css(["display:none;visibility:hidden;padding-bottom:0;"])));
StyledPopoverClose.defaultProps = {
  theme: defaultTheme
};

var PopoverContentWrapper = function PopoverContentWrapper(_ref17) {
  var children = _ref17.children,
      onClose = _ref17.onClose,
      width = _ref17.width,
      dataTest = _ref17.dataTest,
      preferredPosition = _ref17.preferredPosition,
      preferredAlign = _ref17.preferredAlign,
      containerRef = _ref17.containerRef,
      noPadding = _ref17.noPadding,
      overlapped = _ref17.overlapped,
      shown = _ref17.shown,
      fixed = _ref17.fixed,
      actions = _ref17.actions;

  var _useContext = useContext(ModalContext),
      isInsideModal = _useContext.isInsideModal;

  var popover = useRef(null);
  var content = useRef(null);
  var overlay = useRef(null);
  var position = calculatePopoverPosition(preferredPosition, preferredAlign);
  var dimensions = useDimensions({
    containerRef: containerRef,
    popover: popover,
    content: content,
    fixed: fixed
  });
  var verticalPosition = calculateVerticalPosition(position[0], dimensions);
  var horizontalPosition = calculateHorizontalPosition(position[1], dimensions);
  useEffect(function () {
    var timer = setTimeout(function () {
      if (popover.current) {
        popover.current.focus();
      }
    }, 100);
    return function () {
      return clearTimeout(timer);
    };
  }, []);
  useClickOutside(popover, onClose);
  return React.createElement(React.Fragment, null, React.createElement(StyledOverlay, {
    ref: overlay,
    shown: shown,
    isInsideModal: isInsideModal
  }), React.createElement(StyledPopoverParent, {
    shownMobile: shown,
    shown: shown && verticalPosition && horizontalPosition,
    anchor: horizontalPosition,
    position: verticalPosition,
    containerTop: dimensions.containerTop,
    containerLeft: dimensions.containerLeft,
    containerPureTop: dimensions.containerPureTop,
    containerHeight: dimensions.containerHeight,
    containerWidth: dimensions.containerWidth,
    popoverHeight: dimensions.popoverHeight,
    popoverWidth: dimensions.popoverWidth,
    width: width,
    ref: popover,
    tabIndex: "0",
    "data-test": dataTest,
    noPadding: noPadding,
    overlapped: overlapped,
    role: "tooltip",
    fixed: fixed,
    isInsideModal: isInsideModal
  }, React.createElement(StyledPopoverContent, {
    ref: content
  }, children, !actions && React.createElement(StyledPopoverClose, {
    noPadding: noPadding
  }, React.createElement(Button, {
    type: "secondary",
    fullWidth: true,
    onClick: onClose
  }, React.createElement(Translate, {
    tKey: "button_close"
  }))))));
};

export default PopoverContentWrapper;